import React, { useState } from 'react';
import { Plus, X, Save, Package, Users, CreditCard } from 'lucide-react';
import stripeApiService from '../services/stripeApiService';

/**
 * Composant pour créer de nouveaux produits Stripe avec catégorisation
 */
const StripeProductCreator = ({ onProductCreated, onCancel }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [productData, setProductData] = useState({
    name: '',
    description: '',
    category: 'subscription', // subscription, invoice, service
    target: 'employee', // employee, beneficiary, client
    metadata: {
      plan_type: 'monthly', // monthly, yearly, one_time
      service_type: 'general', // consultation, intervention, etc.
      features: 'basic'
    }
  });

  // Réinitialiser le formulaire
  const resetForm = () => {
    setProductData({
      name: '',
      description: '',
      category: 'subscription',
      target: 'employee',
      metadata: {
        plan_type: 'monthly',
        service_type: 'general',
        features: 'basic'
      }
    });
    setError(null);
  };

  // Créer un nouveau produit
  const createProduct = async () => {
    if (!productData.name) {
      setError('Veuillez saisir un nom pour le produit');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const productDataToSend = {
        name: productData.name,
        description: productData.description || undefined,
        metadata: {
          category: productData.category,
          target: productData.target,
          ...productData.metadata
        }
      };

      await stripeApiService.createProduct(productDataToSend);
      
      // Réinitialiser et fermer
      resetForm();
      setShowForm(false);
      
      // Notifier le parent
      if (onProductCreated) {
        onProductCreated();
      }
    } catch (error) {
      console.error('Erreur lors de la création du produit:', error);
      setError(`Erreur lors de la création: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Annuler la création
  const handleCancel = () => {
    resetForm();
    setShowForm(false);
    if (onCancel) {
      onCancel();
    }
  };

  // Options de catégorisation
  const categoryOptions = [
    { value: 'subscription', label: 'Abonnement', icon: CreditCard, description: 'Paiements récurrents pour accès aux fonctionnalités' },
    { value: 'invoice', label: 'Facturation', icon: Users, description: 'Paiements ponctuels pour services rendus' },
    { value: 'service', label: 'Service', icon: Package, description: 'Produits ou services spécifiques' }
  ];

  const targetOptions = [
    { value: 'employee', label: 'Intervenant', description: 'Pour les employés/utilisateurs de la plateforme' },
    { value: 'beneficiary', label: 'Bénéficiaire', description: 'Pour les clients/bénéficiaires des services' }
  ];

  const planTypeOptions = [
    { value: 'monthly', label: 'Mensuel' },
    { value: 'yearly', label: 'Annuel' },
    { value: 'one_time', label: 'Ponctuel' }
  ];

  const serviceTypeOptions = [
    { value: 'general', label: 'Général' },
    { value: 'consultation', label: 'Consultation' },
    { value: 'intervention', label: 'Intervention' },
    { value: 'accompaniment', label: 'Accompagnement' },
    { value: 'training', label: 'Formation' }
  ];

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        title="Créer un nouveau produit"
      >
        <Plus className="w-4 h-4 mr-2" />
        Nouveau produit
      </button>
    );
  }

  return (
    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h5 className="font-semibold text-green-800 dark:text-green-300 text-lg">
          Créer un nouveau produit Stripe
        </h5>
        <button
          onClick={handleCancel}
          className="p-1 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-4">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informations de base */}
        <div className="space-y-4">
          <h6 className="font-medium text-green-700 dark:text-green-400">Informations de base</h6>
          
          {/* Nom du produit */}
          <div>
            <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-1">
              Nom du produit *
            </label>
            <input
              type="text"
              value={productData.name}
              onChange={(e) => setProductData({ ...productData, name: e.target.value })}
              className="w-full px-3 py-2 border border-green-300 dark:border-green-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="ex: Essentiel Mensuel, Consultation Standard"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={productData.description}
              onChange={(e) => setProductData({ ...productData, description: e.target.value })}
              className="w-full px-3 py-2 border border-green-300 dark:border-green-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Description détaillée du produit..."
              rows="3"
            />
          </div>
        </div>

        {/* Catégorisation */}
        <div className="space-y-4">
          <h6 className="font-medium text-green-700 dark:text-green-400">Catégorisation</h6>
          
          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-2">
              Type de produit
            </label>
            <div className="grid grid-cols-1 gap-2">
              {categoryOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    productData.category === option.value
                      ? 'bg-green-100 dark:bg-green-800 border-green-500'
                      : 'bg-white dark:bg-gray-700 border-green-300 dark:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                  }`}
                >
                  <input
                    type="radio"
                    value={option.value}
                    checked={productData.category === option.value}
                    onChange={(e) => setProductData({ ...productData, category: e.target.value })}
                    className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <option.icon className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {option.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {option.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Cible */}
          <div>
            <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-2">
              Public cible
            </label>
            <div className="grid grid-cols-1 gap-2">
              {targetOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    productData.target === option.value
                      ? 'bg-green-100 dark:bg-green-800 border-green-500'
                      : 'bg-white dark:bg-gray-700 border-green-300 dark:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                  }`}
                >
                  <input
                    type="radio"
                    value={option.value}
                    checked={productData.target === option.value}
                    onChange={(e) => setProductData({ ...productData, target: e.target.value })}
                    className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900 dark:text-white block mb-1">
                      {option.label}
                    </span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {option.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Métadonnées spécifiques */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Type de plan (pour abonnements) */}
        {productData.category === 'subscription' && (
          <div>
            <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-1">
              Type d'abonnement
            </label>
            <select
              value={productData.metadata.plan_type}
              onChange={(e) => setProductData({
                ...productData,
                metadata: { ...productData.metadata, plan_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-green-300 dark:border-green-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {planTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Type de service (pour facturation) */}
        {(productData.category === 'invoice' || productData.category === 'service') && (
          <div>
            <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-1">
              Type de service
            </label>
            <select
              value={productData.metadata.service_type}
              onChange={(e) => setProductData({
                ...productData,
                metadata: { ...productData.metadata, service_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-green-300 dark:border-green-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {serviceTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Niveau de fonctionnalités */}
        <div>
          <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-1">
            Niveau de fonctionnalités
          </label>
          <select
            value={productData.metadata.features}
            onChange={(e) => setProductData({
              ...productData,
              metadata: { ...productData.metadata, features: e.target.value }
            })}
            className="w-full px-3 py-2 border border-green-300 dark:border-green-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="basic">Basique</option>
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
            <option value="enterprise">Entreprise</option>
          </select>
        </div>
      </div>

      {/* Aperçu des métadonnées */}
      <div className="mt-4 bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-lg p-4">
        <h6 className="font-medium text-green-700 dark:text-green-400 mb-2">
          Métadonnées qui seront envoyées à Stripe
        </h6>
        <pre className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-3 rounded overflow-x-auto">
          {JSON.stringify({
            category: productData.category,
            target: productData.target,
            ...productData.metadata
          }, null, 2)}
        </pre>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 mt-6">
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Annuler
        </button>
        <button
          onClick={createProduct}
          disabled={saving || !productData.name}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Création...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-1" />
              Créer le produit
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default StripeProductCreator;
